import type { TFunction } from 'i18next';
import { isAbortError } from '../../../utils/errors';
import { extractApiErrors, getApiErrorMessage } from '../../../utils/apiErrors';
import { appDate, appDateTime, isoDateToDisplay } from '../../../utils/appDateFormat';
import { parseApiUtc } from '../../../utils/apiDate';

type KnownUser = { id?: string | number | null; name?: string | null; fullName?: string | null };
// One entry of the server's conflicts list. Rule and shift-set refusals use different subsets of
// these fields (camelCase today; PascalCase tolerated, as the drawer's dialog already does).
type ScheduleConflict = Record<string, unknown>;
type ResultLike = { errors?: unknown; response?: { data?: { errors?: unknown } } };

/**
 * The structured conflicts a ScheduleOverlap refusal carries (406, errors[].details.conflicts).
 * The axios interceptor rejects with the Result body itself, so `err.errors` is the usual shape;
 * an AxiosError (`err.response.data.errors`) is tolerated too.
 */
export function extractScheduleConflicts(err: unknown): ScheduleConflict[] {
  const e = err as ResultLike | null | undefined;
  const errors = Array.isArray(e?.errors) ? e.errors : (Array.isArray(e?.response?.data?.errors) ? e.response.data.errors : []);
  for (const item of errors as Array<Record<string, Record<string, unknown> | undefined> | null>) {
    const details = item?.details || item?.Details;
    const conflicts = details?.conflicts || details?.Conflicts;
    if (Array.isArray(conflicts) && conflicts.length > 0) return conflicts as ScheduleConflict[];
  }
  return [];
}

// "a, b, c (+4)" — the first few, then how many more, so a months-long rule doesn't print every day.
const shortList = (items: string[], max = 3) =>
  items.length > max ? `${items.slice(0, max).join(', ')} (+${items.length - max})` : items.join(', ');

// The day a conflicting occurrence falls on. Shift-set and rule conflicts carry the employee-local wall
// clock ("yyyy-MM-dd HH:mm"; older servers sent rule conflicts as a UTC instant only); the daily-limit
// entries a list of local days.
const conflictDates = (c: ScheduleConflict): string[] => {
  const local = c?.candidateStartLocal || c?.CandidateStartLocal;
  if (typeof local === 'string' && /^\d{4}-\d{2}-\d{2}/.test(local)) return [isoDateToDisplay(local.slice(0, 10))];
  const utcRaw = c?.candidateStartUtc || c?.CandidateStartUtc;
  const utc = typeof utcRaw === 'string' ? parseApiUtc(utcRaw) : null;
  if (utc) return [appDate(utc)];
  const days = c?.dailyLimitExceededDates || c?.DailyLimitExceededDates;
  if (Array.isArray(days)) {
    return days.filter((d) => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)).map((d) => isoDateToDisplay(d.slice(0, 10)));
  }
  return [];
};

// The name to show for a conflict: the one the server sends (the page's user list can miss someone), else the page's.
const knownNames = (users: KnownUser[] | null | undefined): Map<string, string> => {
  const nameById = new Map<string, string>();
  (users || []).forEach((u) => {
    const name = u?.name || u?.fullName;
    if (u?.id != null && name) nameById.set(String(u.id), String(name));
  });
  return nameById;
};
const conflictUserId = (c: ScheduleConflict) => String(c?.userId ?? c?.UserId ?? '');
const conflictName = (c: ScheduleConflict, nameById: Map<string, string>): string => {
  const sent = c?.employeeName ?? c?.EmployeeName;
  return (typeof sent === 'string' && sent) || nameById.get(conflictUserId(c)) || '';
};

// The server's employee-local text ("yyyy-MM-dd HH:mm"), split so a range on one day prints that day once.
const wallClock = (value: unknown): { day: string; time: string } | null => {
  const m = typeof value === 'string' ? /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})/.exec(value.trim()) : null;
  return m ? { day: isoDateToDisplay(m[1]), time: m[2] } : null;
};
// Older servers sent some conflicts as a UTC instant only.
const instantText = (value: unknown): string => {
  const parsed = typeof value === 'string' ? parseApiUtc(value) : null;
  return parsed ? appDateTime(parsed) : '';
};
const timeRange = (start: unknown, end: unknown): string => {
  const from = wallClock(start);
  const to = wallClock(end);
  if (from && to) {
    return from.day === to.day
      ? `${from.day} ${from.time} – ${to.time}`
      : `${from.day} ${from.time} – ${to.day} ${to.time}`;
  }
  return [from ? `${from.day} ${from.time}` : instantText(start), to ? `${to.day} ${to.time}` : instantText(end)]
    .filter(Boolean).join(' – ');
};

export type ConflictRow = {
  key: string;
  /** Sort key: the raw start, whose wall-clock and ISO forms both sort by time. */
  start: string;
  /** Date and time, for display inside an LTR isolate. */
  range: string;
  /** False when a server without overlappingShifts only said which of this schedule's own shifts is refused. */
  isOtherShift: boolean;
  /** The other schedule; null on an other shift means a one-off shift that belongs to no schedule. */
  scheduleName: string | null;
};
export type EmployeeConflicts = { key: string; name: string; rows: ConflictRow[]; dailyLimitDates: string[] };

/**
 * The conflicts payload per employee, holding only what may be shown: name, dates, times, the other schedule's
 * name. Never the shift IDs or sourceType (owner, 2026-11-02). Tolerates any entry shape, because a server from
 * before CO-996 sends no employeeName or overlappingShifts while a deploy is running.
 */
export function groupScheduleConflicts(conflicts: unknown, users: KnownUser[] | null | undefined): EmployeeConflicts[] {
  const nameById = knownNames(users);
  const byEmployee = new Map<string, EmployeeConflicts>();
  (Array.isArray(conflicts) ? conflicts : []).forEach((c: ScheduleConflict) => {
    if (!c || typeof c !== 'object') return;
    const key = conflictUserId(c);
    const employee = byEmployee.get(key)
      ?? { key, name: conflictName(c, nameById), rows: [], dailyLimitDates: [] };
    byEmployee.set(key, employee);
    const add = (start: unknown, end: unknown, isOtherShift: boolean, scheduleName: string | null) => {
      const range = timeRange(start, end);
      const rowKey = `${isOtherShift}|${scheduleName ?? ''}|${range}`;
      if (!range || employee.rows.some((row) => row.key === rowKey)) return;
      employee.rows.push({ key: rowKey, start: String(start ?? ''), range, isOtherShift, scheduleName });
    };
    const others = c.overlappingShifts ?? c.OverlappingShifts;
    if (Array.isArray(others) && others.length > 0) {
      others.forEach((o: ScheduleConflict) => {
        const name = o?.scheduleName ?? o?.ScheduleName;
        add(o?.startLocal ?? o?.StartLocal, o?.endLocal ?? o?.EndLocal, true, typeof name === 'string' && name ? name : null);
      });
    } else {
      add(c.candidateStartLocal ?? c.CandidateStartLocal ?? c.candidateStartUtc ?? c.CandidateStartUtc,
        c.candidateEndLocal ?? c.CandidateEndLocal ?? c.candidateEndUtc ?? c.CandidateEndUtc, false, null);
    }
    // The server adds a daily-limit entry per checked shift, each repeating the days before it.
    const days = c.dailyLimitExceededDates ?? c.DailyLimitExceededDates;
    if (Array.isArray(days)) {
      days.forEach((d) => {
        const shown = typeof d === 'string' ? isoDateToDisplay(d) : '';
        if (shown && !employee.dailyLimitDates.includes(shown)) employee.dailyLimitDates.push(shown);
      });
    }
  });
  // An entry with nothing to show would otherwise print "Former employee" on its own.
  const employees = Array.from(byEmployee.values()).filter((e) => e.name || e.rows.length > 0 || e.dailyLimitDates.length > 0);
  employees.forEach((employee) => employee.rows.sort((a, b) => a.start.localeCompare(b.start)));
  return employees;
}

// RecurringRuleOverlap: the set already runs another period at the same weekly time (the server
// refuses it so two every-N-weeks rules never add up to every week). Its details carry that period's
// dates as yyyy-MM-dd; no end date means open-ended.
const ruleOverlapMessage = (err: unknown, t: TFunction): string | null => {
  const refusal = extractApiErrors(err).find((e) => e?.code === 'RecurringRuleOverlap');
  if (!refusal) return null;
  const details = (refusal.details || {}) as Record<string, unknown>;
  const startRaw = details.otherStartDate ?? details.OtherStartDate;
  const endRaw = details.otherEndDate ?? details.OtherEndDate;
  const start = typeof startRaw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(startRaw) ? isoDateToDisplay(startRaw.slice(0, 10)) : '';
  const end = typeof endRaw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(endRaw) ? isoDateToDisplay(endRaw.slice(0, 10)) : '';
  if (!start) return t('ScheduleRuleOverlapOtherPeriodNoDates');
  return end ? t('ScheduleRuleOverlapOtherPeriod', { start, end }) : t('ScheduleRuleOverlapOtherPeriodOpenEnded', { start });
};

// The server's English descriptions this helper replaces with a translated sentence.
const serverDescriptions = (err: unknown): string[] =>
  extractApiErrors(err).map((e) => (typeof e?.description === 'string' ? e.description.trim() : '')).filter(Boolean);

// A request that never got an answer (offline, DNS, timeout): the interceptor rejects with the raw
// AxiosError, whose message ("Network Error", "timeout of 0ms exceeded") is English and technical.
// Exported because the server may still have carried such a request out: a create flow must not
// clean up after it as if it had been refused.
export const isNoResponse = (err: unknown): boolean => {
  const e = err as { isAxiosError?: boolean; response?: unknown; code?: string } | null | undefined;
  return !!e && !e.response && (e.isAxiosError === true || e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT');
};

/**
 * What to tell the user when a schedule save is refused. A ScheduleOverlap refusal names the
 * employees and days from its conflicts payload, with the server's name or else one the page holds
 * (a generic overlap sentence when there is neither). Anything else goes through getApiErrorMessage,
 * which keeps a specific server sentence but replaces Error.cs boilerplate with the translated
 * `fallback`. `usersSaved` says the employees were written before the refusal (see DeferredUsers in
 * splitConfirm.tsx): "nothing was saved" would then be false, so the overlap sentence does not say it, and the
 * caller adds what did land.
 */
export function describeScheduleSaveError(
  err: unknown,
  t: TFunction,
  users: KnownUser[] | null | undefined,
  fallback: string,
  usersSaved = false,
): string {
  if (isAbortError(err)) return '';
  if (isNoResponse(err)) return t('Could not reach the server. Please check your internet connection and try again.');
  const ruleOverlap = ruleOverlapMessage(err, t);
  if (ruleOverlap) return ruleOverlap;
  const descriptions = serverDescriptions(err);
  if (descriptions.some((d) => d.startsWith('EndDateUtc must be on or after StartDateUtc'))) {
    return t('ScheduleEndDateBeforeStart');
  }
  const conflicts = extractScheduleConflicts(err);
  const isOverlap = conflicts.length > 0 || descriptions.some((d) => d.startsWith('ScheduleOverlap'));
  if (conflicts.length > 0) {
    const nameById = knownNames(users);
    const names: string[] = [];
    const dates: string[] = [];
    conflicts.forEach((c) => {
      const name = conflictName(c, nameById);
      if (name && !names.includes(name)) names.push(name);
      conflictDates(c).forEach((d) => { if (d && !dates.includes(d)) dates.push(d); });
    });
    if (names.length > 0 && dates.length > 0) {
      return t(usersSaved ? 'ScheduleOverlapRuleNotSaved' : 'ScheduleOverlapNotSaved', { names: shortList(names), dates: shortList(dates) });
    }
  }
  // "ScheduleOverlap conflicts detected[: …]" is English and names nobody; say the same thing translated.
  if (isOverlap) return t('ScheduleOverlapNoNamesNotSaved');
  return getApiErrorMessage(err, t, fallback, ' ');
}
